import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, MapPin, Calendar, Clock, Users, Euro, Share2, UserPlus, Shuffle, X, CheckCircle, Vote, Lock } from "lucide-react";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import api from "@/services/api";
import { useAuth } from "@/context/AuthContext";
import { ensureNotifications } from "@/services/push";
import CloseMatchModal from "@/components/CloseMatchModal";
import { TeamsDisplay } from "@/components/MatchDetailParts";
import MatchChat from "@/components/MatchChat";

export default function MatchDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [match, setMatch] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [teams, setTeams] = useState(null);
  const [votes, setVotes] = useState(null);
  const [ratings, setRatings] = useState(null);
  const [isJoining, setIsJoining] = useState(false);
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [scorePulseKey, setScorePulseKey] = useState(0); // bump to retrigger animation
  const prevScoreRef = useRef({ a: null, b: null });

  const loadMatch = useCallback(async (silent = false) => {
    try {
      if (!silent) setIsLoading(true);
      const data = await api.getMatch(id);
      // Detect score change → trigger pulse animation
      if (silent && data && (data.score_a !== prevScoreRef.current.a || data.score_b !== prevScoreRef.current.b)
          && prevScoreRef.current.a !== null) {
        setScorePulseKey(k => k + 1);
      }
      prevScoreRef.current = { a: data?.score_a ?? null, b: data?.score_b ?? null };
      setMatch(data);
      if (data.status === "completed") {
        try {
          const votesData = await api.getMatchVotes(id);
          setVotes(votesData);
        } catch (e) {
          console.warn("Votes not available:", e.message);
        }
        try {
          const ratingsData = await api.getMatchRatings(id);
          setRatings(ratingsData);
        } catch (e) {
          console.warn("Ratings not available:", e.message);
        }
      }
    } catch (error) {
      if (!silent) {
        toast.error("Match non trouve");
        navigate("/matches");
      }
    } finally {
      if (!silent) setIsLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    loadMatch();
    // Auto-refresh: silent poll every 15s while page is visible
    const intervalId = setInterval(() => {
      if (!document.hidden) loadMatch(true);
    }, 15000);
    return () => clearInterval(intervalId);
  }, [loadMatch]);

  if (isLoading || !match) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="animate-spin" style={{ width: '32px', height: '32px', border: '3px solid #333', borderTopColor: '#22c55e', borderRadius: '50%' }} />
      </div>
    );
  }

  const players = match.players || [];
  const hasJoined = players.some(p => p.id === user?.id);
  const isOrganizer = match.organizer_id === user?.id;
  const progressPercent = (match.current_players / match.max_players) * 100;
  const isFull = match.current_players >= match.max_players;
  const isCompleted = match.status === "completed";

  const handleShare = () => {
    const url = `https://matchsups.com/api/og/match/${match.id}`;
    const message = `Je t'invite au match "${match.title}" le ${match.date} a ${match.time} - ${match.location}.\n\nRejoins-nous : ${url}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, "_blank");
  };

  const handleJoin = async () => {
    // Block joining unless notifications are active (shows the gate modal).
    if (!ensureNotifications()) return;
    try {
      setIsJoining(true);
      const updated = await api.joinMatch(id);
      setMatch(updated);
      toast.success("Tu as rejoint le match!");
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsJoining(false);
    }
  };

  const handleLeave = async () => {
    try {
      setIsJoining(true);
      const updated = await api.leaveMatch(id);
      setMatch(updated);
      toast.success("Tu as quitte le match");
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsJoining(false);
    }
  };

  const handleBalanceTeams = async () => {
    try {
      const balanced = await api.balanceTeams(id);
      setTeams(balanced);
      toast.success("Equipes equilibrees!");
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleMatchClosed = (updatedMatch) => {
    setMatch(updatedMatch);
    setShowCloseModal(false);
  };

  const handleVoteMVP = async (playerId) => {
    try {
      const result = await api.voteMVP(id, playerId);
      setVotes(result);
      toast.success("Vote enregistre!");
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleRatePlayer = async (playerId, score) => {
    try {
      await api.ratePlayer(id, playerId, score);
      const updated = await api.getMatchRatings(id);
      setRatings(updated);
      toast.success(`Note ${score}/10 enregistree!`);
    } catch (error) {
      toast.error(error.message);
    }
  };

  // Determine opponents for rating
  const getOpponents = () => {
    if (!isCompleted || !match.team_a?.length || !match.team_b?.length) return [];
    const myTeam = match.team_a.includes(user?.id) ? 'a' : match.team_b.includes(user?.id) ? 'b' : null;
    if (!myTeam) return [];
    const opponentIds = myTeam === 'a' ? match.team_b : match.team_a;
    return players.filter(p => opponentIds.includes(p.id));
  };

  return (
    <div data-testid="match-detail-page" className="min-h-screen bg-black">
      {/* Header */}
      <header style={{ position: 'sticky', top: 0, zIndex: 20, backgroundColor: 'rgba(0,0,0,0.95)', backdropFilter: 'blur(12px)', borderBottom: '1px solid #1a1a1a' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 16px' }}>
          <button data-testid="back-button" onClick={() => navigate(-1)} style={{ padding: '8px', background: 'none', border: 'none', color: '#fff', cursor: 'pointer' }}>
            <ArrowLeft size={20} />
          </button>
          <h1 className="font-heading" style={{ fontSize: '15px', fontWeight: 600, color: '#fff', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {match.title}
          </h1>
          {isCompleted && (
            <Badge style={{ backgroundColor: 'rgba(107,114,128,0.2)', color: '#9ca3af', border: 'none' }}>Termine</Badge>
          )}
          {match.is_private && (
            <Badge data-testid="detail-badge-private" style={{ backgroundColor: 'rgba(148,163,184,0.15)', color: '#cbd5e1', border: '1px solid #475569', display: 'flex', alignItems: 'center', gap: '3px' }}>
              <Lock size={11} /> Prive
            </Badge>
          )}
          <button onClick={handleShare} style={{ padding: '8px', background: 'none', border: 'none', color: '#22c55e', cursor: 'pointer' }}>
            <Share2 size={18} />
          </button>
        </div>
      </header>

      {/* Score Banner (completed matches) */}
      {isCompleted && match.score_a != null && (
        <section data-testid="match-score-banner" style={{ padding: '16px' }}>
          <div style={{
            background: 'linear-gradient(135deg, rgba(34,197,94,0.1), rgba(59,130,246,0.1))',
            border: '1px solid #333', borderRadius: '16px', padding: '20px', textAlign: 'center',
          }}>
            <p style={{ fontSize: '10px', color: '#6b7280', textTransform: 'uppercase', marginBottom: '8px', fontWeight: 600 }}>Score Final</p>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '16px' }}>
              <div>
                <p style={{ fontSize: '10px', color: '#22c55e', marginBottom: '4px' }}>EQ. A</p>
                <span key={`a-${scorePulseKey}`} className={scorePulseKey > 0 ? 'score-pulse' : ''} style={{ fontSize: '36px', fontWeight: 700, color: '#fff' }}>{match.score_a}</span>
              </div>
              <span style={{ fontSize: '24px', color: '#6b7280', fontWeight: 300 }}>-</span>
              <div>
                <p style={{ fontSize: '10px', color: '#3b82f6', marginBottom: '4px' }}>EQ. B</p>
                <span key={`b-${scorePulseKey}`} className={scorePulseKey > 0 ? 'score-pulse' : ''} style={{ fontSize: '36px', fontWeight: 700, color: '#fff' }}>{match.score_b}</span>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Match Info */}
      <section style={{ padding: '20px 16px' }}>
        <div style={{ backgroundColor: '#111', border: '0.5px solid #333', borderRadius: '16px', padding: '20px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <MapPin size={16} color="#22c55e" />
              <span style={{ fontSize: '14px', color: '#d1d5db' }}>{match.location_name || match.location}</span>
              {match.city && (
                <span data-testid="detail-match-city" style={{
                  fontSize: '12px', fontWeight: 600, color: '#22c55e',
                  backgroundColor: 'rgba(34,197,94,0.1)', padding: '2px 10px', borderRadius: '9999px',
                }}>
                  {match.city}
                </span>
              )}
              {(match.country === 'CH' || match.country === 'BE') && (
                <span data-testid="detail-match-flag" title={match.country === 'CH' ? 'Suisse' : 'Belgique'} style={{ fontSize: '14px', lineHeight: 1 }}>
                  {match.country === 'CH' ? '🇨🇭' : '🇧🇪'}
                </span>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Calendar size={16} color="#22c55e" />
                <span style={{ fontSize: '14px', color: '#d1d5db' }}>{match.date}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Clock size={16} color="#22c55e" />
                <span style={{ fontSize: '14px', color: '#d1d5db' }}>{match.time}</span>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <Euro size={16} color="#22c55e" />
              <span style={{ fontSize: '14px', fontWeight: 600, color: '#22c55e' }}>{match.price}</span>
            </div>
          </div>

          {/* Progress */}
          <div style={{ marginBottom: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Users size={14} color="#22c55e" />
                <span style={{ fontSize: '13px', fontWeight: 500, color: '#fff' }}>
                  {match.current_players}/{match.max_players} joueurs
                </span>
              </div>
              {isFull && !isCompleted && (
                <Badge style={{ backgroundColor: 'rgba(239,68,68,0.1)', color: '#f87171' }}>Complet</Badge>
              )}
            </div>
            <Progress value={progressPercent} className="h-1.5" style={{ backgroundColor: '#222' }} indicatorClassName="bg-[#22c55e]" />
          </div>

          {/* Action Buttons */}
          {!isCompleted ? (
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
              {hasJoined ? (
                <button data-testid="leave-match-btn" onClick={handleLeave} disabled={isJoining}
                  style={{ flex: '0 1 140px', height: '44px', borderRadius: '12px', fontWeight: 500, fontSize: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', cursor: 'pointer', border: '1px solid rgba(239,68,68,0.3)', backgroundColor: 'rgba(239,68,68,0.1)', color: '#f87171' }}>
                  <X size={16} /> Se desinscrire
                </button>
              ) : (
                <button data-testid="join-match-btn" onClick={handleJoin} disabled={isJoining || isFull}
                  style={{ flex: '0 1 140px', height: '44px', borderRadius: '12px', fontWeight: 500, fontSize: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', cursor: isFull ? 'not-allowed' : 'pointer', border: 'none', backgroundColor: isFull ? '#222' : '#22c55e', color: isFull ? '#6b7280' : '#fff' }}>
                  <UserPlus size={16} /> {isFull ? 'Complet' : 'Rejoindre'}
                </button>
              )}

              <button data-testid="balance-teams-btn" onClick={handleBalanceTeams}
                style={{ height: '44px', padding: '0 16px', borderRadius: '12px', fontWeight: 500, fontSize: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', cursor: 'pointer', border: '1px solid #333', backgroundColor: 'transparent', color: '#fff' }}>
                <Shuffle size={16} /> Equilibrer
              </button>

              {isOrganizer && players.length >= 2 && (
                <button data-testid="close-match-btn" onClick={() => setShowCloseModal(true)}
                  style={{ height: '44px', padding: '0 16px', borderRadius: '12px', fontWeight: 500, fontSize: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', cursor: 'pointer', border: 'none', backgroundColor: '#6b7280', color: '#fff' }}>
                  <CheckCircle size={16} /> Terminer
                </button>
              )}
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '12px', backgroundColor: 'rgba(107,114,128,0.1)', borderRadius: '12px' }}>
              <p style={{ color: '#9ca3af', fontSize: '14px' }}>Match termine - Votez pour le MVP!</p>
            </div>
          )}
        </div>
      </section>

      {/* Balanced Teams */}
      <TeamsDisplay teams={teams} />

      {/* Players List */}
      <section style={{ padding: '0 16px 24px 16px' }}>
        <h2 className="font-heading" style={{ fontSize: '14px', fontWeight: 600, color: '#fff', marginBottom: '12px' }}>
          {isCompleted ? 'Vote MVP' : `Joueurs inscrits (${players.length})`}
        </h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {players.length > 0 ? players.map((player) => (
            <div
              key={player.id}
              data-testid={`player-item-${player.id}`}
              style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '16px', backgroundColor: '#111', border: '0.5px solid #333', borderRadius: '12px', cursor: 'pointer', touchAction: 'manipulation' }}
              onClick={() => navigate(`/profile/${player.id}`)}
            >
              <Avatar style={{ width: '44px', height: '44px', border: '1px solid #444' }}>
                <AvatarFallback style={{ backgroundColor: '#22c55e', color: '#fff', fontWeight: 700 }}>
                  {player.name?.charAt(0)}
                </AvatarFallback>
              </Avatar>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <p style={{ fontWeight: 500, color: '#fff', fontSize: '14px' }}>{player.name}</p>
                  {player.position && (
                    <span data-testid={`position-badge-${player.id}`} style={{
                      fontSize: '10px', fontWeight: 700, padding: '2px 6px', borderRadius: '4px',
                      backgroundColor: player.position === 'Attaquant' ? 'rgba(239,68,68,0.15)' :
                        player.position === 'Milieu' ? 'rgba(234,179,8,0.15)' :
                        player.position === 'Defenseur' ? 'rgba(59,130,246,0.15)' : 'rgba(168,85,247,0.15)',
                      color: player.position === 'Attaquant' ? '#f87171' :
                        player.position === 'Milieu' ? '#eab308' :
                        player.position === 'Defenseur' ? '#60a5fa' : '#a855f7',
                    }}>
                      {player.position.charAt(0)}
                    </span>
                  )}
                </div>
                <p style={{ fontSize: '12px', color: '#6b7280' }}>
                  {player.goals || 0} buts · {player.matches_played || 0} matchs
                  {match.player_goals?.[player.id] > 0 && (
                    <span style={{ color: '#22c55e', marginLeft: '6px' }}>+{match.player_goals[player.id]} ce match</span>
                  )}
                </p>
              </div>
              <span style={{ fontSize: '18px', fontWeight: 700, color: '#22c55e' }}>
                {player.rating?.toFixed(1) || '5.0'}
              </span>

              {isCompleted && hasJoined && !votes?.has_voted && player.id !== user?.id && (
                <button
                  data-testid={`vote-mvp-${player.id}`}
                  onClick={(e) => { e.stopPropagation(); handleVoteMVP(player.id); }}
                  style={{ padding: '8px 12px', borderRadius: '8px', backgroundColor: '#22c55e', color: '#fff', border: 'none', fontSize: '12px', fontWeight: 500, cursor: 'pointer' }}
                >
                  <Vote size={14} />
                </button>
              )}

              {isCompleted && votes?.votes_count?.[player.id] > 0 && (
                <Badge style={{ backgroundColor: 'rgba(234,179,8,0.2)', color: '#eab308' }}>
                  {votes.votes_count[player.id]} vote{votes.votes_count[player.id] > 1 ? 's' : ''}
                </Badge>
              )}
            </div>
          )) : (
            <div style={{ textAlign: 'center', padding: '32px 0' }}>
              <div style={{ fontSize: '28px', marginBottom: '8px' }}>👥</div>
              <p style={{ color: '#9ca3af', fontSize: '14px' }}>Aucun joueur inscrit</p>
            </div>
          )}
        </div>
      </section>

      {/* Private in-match chat — placed under the players list, joined players only */}
      {hasJoined && <MatchChat matchId={id} />}

      {/* Rating Section (Opponents only) */}
      {isCompleted && hasJoined && match.team_a?.length > 0 && (
        <section data-testid="rating-section" style={{ padding: '0 16px 24px 16px' }}>
          <h2 className="font-heading" style={{ fontSize: '14px', fontWeight: 600, color: '#fff', marginBottom: '4px' }}>
            Noter tes adversaires
          </h2>
          <p style={{ fontSize: '11px', color: '#6b7280', marginBottom: '12px' }}>
            Tape sur un chiffre de 0 a 10 pour noter chaque adversaire.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {getOpponents().map(opponent => {
              const alreadyRated = ratings?.ratings?.[opponent.id];
              return (
                <div key={opponent.id} style={{
                  padding: '14px',
                  backgroundColor: '#111', border: '0.5px solid #333', borderRadius: '12px',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: alreadyRated != null ? 0 : '12px' }}>
                    <div style={{ width: '36px', height: '36px', borderRadius: '50%', backgroundColor: '#22c55e', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', fontWeight: 700, color: '#fff' }}>
                      {opponent.name?.charAt(0)}
                    </div>
                    <div style={{ flex: 1 }}>
                      <p style={{ fontSize: '13px', fontWeight: 500, color: '#fff' }}>{opponent.name}</p>
                    </div>
                    {alreadyRated != null && (
                      <span data-testid={`rating-result-${opponent.id}`} style={{
                        fontSize: '14px', fontWeight: 700, color: '#eab308',
                        backgroundColor: 'rgba(234,179,8,0.12)',
                        padding: '4px 10px', borderRadius: '999px',
                      }}>
                        {alreadyRated}/10
                      </span>
                    )}
                  </div>

                  {alreadyRated == null && (
                    <div
                      data-testid={`rating-input-${opponent.id}`}
                      role="radiogroup"
                      aria-label={`Noter ${opponent.name}`}
                      style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(11, 1fr)',
                        gap: '4px',
                      }}
                    >
                      {[0,1,2,3,4,5,6,7,8,9,10].map(n => {
                        const tone = n <= 3 ? '#ef4444' : n <= 6 ? '#eab308' : '#22c55e';
                        return (
                          <button
                            key={n}
                            type="button"
                            data-testid={`rate-${opponent.id}-${n}`}
                            onClick={() => handleRatePlayer(opponent.id, n)}
                            aria-label={`Note ${n} sur 10`}
                            style={{
                              aspectRatio: '1 / 1',
                              minHeight: '32px',
                              minWidth: '0',
                              borderRadius: '50%',
                              border: `1px solid ${tone}55`,
                              backgroundColor: '#0a0a0a',
                              color: tone,
                              fontSize: '13px',
                              fontWeight: 700,
                              padding: 0,
                              cursor: 'pointer',
                              fontFamily: 'inherit',
                              touchAction: 'manipulation',
                              transition: 'transform 0.1s ease, background-color 0.15s ease',
                            }}
                            onTouchStart={(e) => { e.currentTarget.style.transform = 'scale(0.92)'; e.currentTarget.style.backgroundColor = `${tone}22`; }}
                            onTouchEnd={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
                            onMouseDown={(e) => { e.currentTarget.style.transform = 'scale(0.92)'; e.currentTarget.style.backgroundColor = `${tone}22`; }}
                            onMouseUp={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
                          >
                            {n}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
            {getOpponents().length === 0 && (
              <p style={{ fontSize: '12px', color: '#6b7280', textAlign: 'center', padding: '12px' }}>
                Les equipes n'ont pas ete definies pour ce match.
              </p>
            )}
          </div>
        </section>
      )}

      {/* Close Match Modal */}
      {showCloseModal && (
        <CloseMatchModal
          match={match}
          onClose={() => setShowCloseModal(false)}
          onMatchClosed={handleMatchClosed}
        />
      )}
    </div>
  );
}
